import { Button } from '../../../shared/components/ui/button'
import type { FailedSyncItem } from '../../../shared/store/slices/syncSlice'
import { RotateCwIcon, TrashIcon, TriangleAlertIcon } from 'lucide-react'

const ENTITY_LABELS: Record<FailedSyncItem['entity'], string> = {
  job: 'job',
  node: 'tree',
  photo: 'photo',
  report: 'report',
}

const OPERATION_LABELS: Record<FailedSyncItem['operation'], string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
}

const formatWhen = (timestamp: number): string => {
  const minutes = Math.floor((Date.now() - timestamp) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

interface SyncIssuesPanelProps {
  items: FailedSyncItem[]
  isOnline: boolean
  onRetry: (id: string) => void
  onDiscard: (id: string) => void
}

/**
 * Dead-letter list for the sync status dialog: every change that exhausted
 * its automatic retries, with per-item retry and discard actions (REL-01).
 */
export const SyncIssuesPanel = ({ items, isOnline, onRetry, onDiscard }: SyncIssuesPanelProps) => {
  if (items.length === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <TriangleAlertIcon className="h-4 w-4 text-destructive" />
        <p className="text-sm font-medium">Sync issues</p>
      </div>

      <div className="max-h-56 space-y-2 overflow-y-auto">
        {items.map((item) => (
          <div key={item.id} className="rounded-lg border border-destructive/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {OPERATION_LABELS[item.operation]} {ENTITY_LABELS[item.entity]}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatWhen(item.timestamp)} · {item.retryCount}{' '}
                  {item.retryCount === 1 ? 'attempt' : 'attempts'}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRetry(item.id)}
                  disabled={!isOnline}
                  aria-label="Retry"
                >
                  <RotateCwIcon className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDiscard(item.id)}
                  aria-label="Discard"
                >
                  <TrashIcon className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            </div>
            {item.error && (
              <p className="mt-1 truncate text-xs text-destructive/80" title={item.error}>
                {item.error}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
